// Owner Customers page hooks (smoke-test fixes, 2026-05-20).
//
// Three surfaces:
//   - useRecentCustomers : GET /api/owner/customers — recent list (≤50)
//   - useCustomerSearch  : GET /api/owner/customers/search?q= — exact match
//   - useRecoverPass     : POST /api/cards/:id/save-tokens — re-mint a
//                          wallet save link so a customer who lost their
//                          phone (or removed the pass) can re-add it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ownerApi } from '../ownerApi.js'

/** Card status — 'completed' only occurs under completion_behavior
 *  'stay_completed'; 'reset' programs recycle the same card in place. */
export type CustomerCardStatus = 'active' | 'completed'

/** One customer row — shared by the recent list and search results. */
export interface CustomerRow {
  customer_id: string
  first_name: string | null
  email: string | null
  phone: string | null
  card_id: string
  card_code: string
  stamps_count: number
  status: CustomerCardStatus
  total_stamps: number
  /** When the card was issued (ISO) — the owner-facing "joined" timestamp. */
  created_at: string
  /** Customer lifecycle (2026-09-05) — badge only; issueStamp is the real
   *  gate. Revoked/erased customers stay in the list (same reasoning as a
   *  completed card staying visible) so the owner can still find + unrevoke
   *  them. */
  is_revoked: boolean
  is_erased: boolean
}

interface RecentCustomersResponse {
  customers: CustomerRow[]
}

/** The search endpoint predates this page and omits email/phone. */
export interface CustomerSearchRow {
  customer_id: string
  first_name: string | null
  card_id: string
  card_code: string
  stamps_count: number
  status: CustomerCardStatus
  total_stamps: number
  created_at: string
  is_revoked: boolean
  is_erased: boolean
}

interface SearchResponse {
  results: CustomerSearchRow[]
}

interface RecoverPassResponse {
  token: string
  save_url: string
  expires_at: string
}

export const recentCustomersKey = ['owner', 'customers', 'recent'] as const

export function useRecentCustomers() {
  return useQuery<RecentCustomersResponse>({
    queryKey: recentCustomersKey,
    queryFn: () => ownerApi.get<RecentCustomersResponse>('/owner/customers'),
    staleTime: 30_000,
  })
}

/**
 * Partial-match search (name / email / phone / card code, case-insensitive
 * "contains") — a complete correctly-formatted card code still matches
 * exactly. `enabled` is false until the query is long enough to be a useful
 * partial match (the backend ignores anything under 2 chars), so typing
 * doesn't fire a request per keystroke.
 */
export function useCustomerSearch(query: string) {
  const q = query.trim()
  return useQuery<SearchResponse>({
    queryKey: ['owner', 'customers', 'search', q],
    queryFn: () =>
      ownerApi.get<SearchResponse>(
        `/owner/customers/search?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.length >= 2,
    staleTime: 15_000,
  })
}

/**
 * Re-mint a wallet save link for an existing card. The returned save_url is
 * re-usable until it expires in 10 minutes — the page shows it as a QR (scan in
 * person) plus a copyable link (send over chat). Generate again if it
 * expires before the customer uses it.
 */
export function useRecoverPass() {
  return useMutation<RecoverPassResponse, Error, string>({
    mutationFn: (cardId: string) =>
      ownerApi.post<RecoverPassResponse>(
        `/cards/${encodeURIComponent(cardId)}/save-tokens`,
      ),
  })
}

/**
 * Manually reset a completed 'stay_completed' card to a fresh cycle. The
 * server returns the updated card; on success we invalidate the customer
 * lists so the row's status flips back to active. 422 CARD_NOT_RESETTABLE
 * if the card doesn't qualify.
 */
export function useResetCard() {
  const qc = useQueryClient()
  return useMutation<{ card: { id: string } }, Error, string>({
    mutationFn: (cardId: string) =>
      ownerApi.post<{ card: { id: string } }>(
        `/cards/${encodeURIComponent(cardId)}/reset`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['owner', 'customers'] })
    },
  })
}

// ----- Customer lifecycle (2026-09-05) -----

export interface AdjustStampsInput {
  cardId: string
  delta: number
  /** Independent correction to the current cycle's secret-tagged count.
   *  Only meaningful (and only ever non-zero) when secret-menu tagging is
   *  enabled for this card's tenant/program. */
  secretDelta: number
  reason: string
}

/**
 * Owner-only manual stamp correction — signed delta + required reason (e.g.
 * undo an accidental double-scan), plus an independent secret-stamp count
 * correction. 422 STAMP_ADJUSTMENT_OUT_OF_RANGE / SECRET_STAMP_ADJUSTMENT_
 * OUT_OF_RANGE if either result would fall outside its valid range.
 */
export function useAdjustStamps() {
  const qc = useQueryClient()
  return useMutation<{ card: { id: string } }, Error, AdjustStampsInput>({
    mutationFn: ({ cardId, delta, secretDelta, reason }) =>
      ownerApi.post<{ card: { id: string } }>(
        `/cards/${encodeURIComponent(cardId)}/stamp-adjustment`,
        { delta, secret_delta: secretDelta, reason },
      ),
    onSuccess: (_data, { cardId }) => {
      void qc.invalidateQueries({ queryKey: ['owner', 'customers'] })
      void qc.invalidateQueries({ queryKey: ['owner', 'card-detail', cardId] })
    },
  })
}

interface CustomerLifecycleResponse {
  customer: { id: string; deactivated_at: string | null; erased_at: string | null }
}

/** Reversible business block — stops the customer from earning new stamps. */
export function useRevokeCustomer() {
  const qc = useQueryClient()
  return useMutation<
    CustomerLifecycleResponse,
    Error,
    { customerId: string; cardId: string; reason: string }
  >({
    mutationFn: ({ customerId, reason }) =>
      ownerApi.post<CustomerLifecycleResponse>(
        `/owner/customers/${encodeURIComponent(customerId)}/revoke`,
        { reason },
      ),
    onSuccess: (_data, { cardId }) => {
      void qc.invalidateQueries({ queryKey: ['owner', 'customers'] })
      void qc.invalidateQueries({ queryKey: ['owner', 'card-detail', cardId] })
    },
  })
}

export function useUnrevokeCustomer() {
  const qc = useQueryClient()
  return useMutation<
    CustomerLifecycleResponse,
    Error,
    { customerId: string; cardId: string }
  >({
    mutationFn: ({ customerId }) =>
      ownerApi.post<CustomerLifecycleResponse>(
        `/owner/customers/${encodeURIComponent(customerId)}/unrevoke`,
      ),
    onSuccess: (_data, { cardId }) => {
      void qc.invalidateQueries({ queryKey: ['owner', 'customers'] })
      void qc.invalidateQueries({ queryKey: ['owner', 'card-detail', cardId] })
    },
  })
}

/** Irreversible RA 10173 data-erasure request — anonymizes the customer row
 *  (nulls PII), implies revoke. No "un-erase" mutation exists. */
export function useEraseCustomerData() {
  const qc = useQueryClient()
  return useMutation<
    CustomerLifecycleResponse,
    Error,
    { customerId: string; cardId: string; requestedVia: string }
  >({
    mutationFn: ({ customerId, requestedVia }) =>
      ownerApi.post<CustomerLifecycleResponse>(
        `/owner/customers/${encodeURIComponent(customerId)}/erase`,
        { requested_via: requestedVia },
      ),
    onSuccess: (_data, { cardId }) => {
      void qc.invalidateQueries({ queryKey: ['owner', 'customers'] })
      void qc.invalidateQueries({ queryKey: ['owner', 'card-detail', cardId] })
    },
  })
}
